import type { FrameworkClass } from '../framework/framework.d';
import type { CodeSnippetImport } from '../Components/CodeEditor/types';
import { DRIVE_UNIT_PORT_SNIPPET_CLASS_NAMES, findFrameworkClassWithDriveUnitPort } from './buildDriveUnitPortOccupanciesFromHardwareCards';
import { getDriveUnitPortInitEntries } from './electronicsPortFromProgramCode';

export interface PendingDeviceSnippetPortDrop {
  /** Localized snippet title used for completion announcements. */
  snippetTitle?: string;
  code: string;
  targetLine: number;
  mechanismClass: FrameworkClass;
  parameterName: string;
  /** Constructor keyword as it appears in the snippet (from framework `constructor_arg`). */
  snippetConstructorKw: string;
  variableName: string;
  imports?: CodeSnippetImport[];
}

/** Fields required to rewrite the port literal in snippet code. */
export type DeviceSnippetPortDropCodeInput = Pick<PendingDeviceSnippetPortDrop, 'code' | 'snippetConstructorKw'>;

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * When the user drops an electronics/mechanisms snippet that pins a drive-unit port,
 * we open a port picker instead of inserting a fixed port immediately.
 */
export function parseDeviceSnippetPortDrop(code: string, snippetType: string | undefined): Omit<PendingDeviceSnippetPortDrop, 'targetLine'> | null {
  if (snippetType !== 'electronic' && snippetType !== 'mechanism') return null;

  const firstLine = code.trim().split('\n')[0] ?? '';
  const headerMatch = firstLine.match(new RegExp(`^\\s*([A-Za-z_]\\w*)\\s*=\\s*(${DRIVE_UNIT_PORT_SNIPPET_CLASS_NAMES.join('|')})\\s*\\(`));
  if (!headerMatch) return null;

  const variableName = headerMatch[1];
  const className = headerMatch[2];
  const mechanismClass = findFrameworkClassWithDriveUnitPort(className);
  if (!mechanismClass) return null;

  const portInits = getDriveUnitPortInitEntries(mechanismClass);
  if (!portInits.length) return null;

  const constructorArgPattern = portInits.map((entry) => escapeRegExp(entry.constructorArg)).join('|');
  const portMatch = firstLine.match(new RegExp(`\\b(${constructorArgPattern})\\s*=\\s*(\\d+)\\b`));
  if (!portMatch) return null;

  const snippetConstructorKw = portMatch[1];
  const matchedInit = portInits.find((entry) => entry.constructorArg === snippetConstructorKw);
  if (!matchedInit) return null;

  return {
    code,
    mechanismClass,
    parameterName: matchedInit.parameter,
    snippetConstructorKw,
    variableName,
  };
}

export function applyDeviceSnippetPortToCode(pending: DeviceSnippetPortDropCodeInput, port: number): string {
  return pending.code.replace(
    new RegExp(`(\\b${escapeRegExp(pending.snippetConstructorKw)}\\s*=\\s*)\\d+`),
    (_, prefix: string) => `${prefix}${port}`
  );
}
